// services/school-ui/src/pages/chat/attachments/attachmentsApi.ts
//
// Thin fetch wrappers around the kernel attachment endpoints. The
// shape matches the contract pinned at nova-os#434.
//
// upload  : POST   /v1/conversations/{conversation_id}/attachments
// delete  : DELETE /v1/conversations/{conversation_id}/attachments/{id}
// (download is a plain GET on the returned download_url — no wrapper
//  needed; the chip just opens it in a new tab.)

import type { AttachmentRef } from "./types";

export class AttachmentApiError extends Error {
  constructor(
    message: string,
    /** HTTP status; 0 if the request itself failed. */
    public readonly status: number,
    /** "validation" → 413/415/409 from the kernel (recoverable, user-facing).
     *  "auth"       → 401/403.
     *  "missing"    → 404/410 (e.g. attachment already deleted).
     *  "network"    → fetch threw (status=0). */
    public readonly kind: "validation" | "auth" | "missing" | "network" | "unknown",
  ) {
    super(message);
    this.name = "AttachmentApiError";
  }
}

function classifyStatus(status: number): AttachmentApiError["kind"] {
  if (status === 401 || status === 403) return "auth";
  if (status === 404 || status === 410) return "missing";
  if (status === 409 || status === 413 || status === 415) return "validation";
  return "unknown";
}

export async function uploadAttachment(
  authHeader: Record<string, string>,
  conversationId: string,
  file: File,
): Promise<AttachmentRef> {
  const form = new FormData();
  form.append("file", file, file.name);

  let res: Response;
  try {
    res = await fetch(
      `/v1/conversations/${encodeURIComponent(conversationId)}/attachments`,
      {
        method: "POST",
        headers: authHeader, // do NOT set Content-Type — FormData sets boundary
        body: form,
      },
    );
  } catch (e) {
    throw new AttachmentApiError((e as Error).message, 0, "network");
  }

  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new AttachmentApiError(
      body.error ?? `upload failed: ${res.status}`,
      res.status,
      classifyStatus(res.status),
    );
  }
  return (await res.json()) as AttachmentRef;
}

export async function deleteAttachment(
  authHeader: Record<string, string>,
  conversationId: string,
  attachmentId: string,
): Promise<void> {
  let res: Response;
  try {
    res = await fetch(
      `/v1/conversations/${encodeURIComponent(conversationId)}/attachments/${encodeURIComponent(attachmentId)}`,
      { method: "DELETE", headers: authHeader },
    );
  } catch (e) {
    throw new AttachmentApiError((e as Error).message, 0, "network");
  }

  if (res.status === 404 || res.status === 410) {
    // Already gone — idempotent success.
    return;
  }
  if (!res.ok) {
    throw new AttachmentApiError(
      `delete failed: ${res.status}`,
      res.status,
      classifyStatus(res.status),
    );
  }
}
